/**
 * Per-invocation cost estimation for the support console.
 *
 * The `ai_invocation` row records a single aggregate token total per scan
 * (`inputTokens` / `outputTokens`) summed across every model call the scan
 * made — for a tiled panel-scan that is the identity pass + the per-photo
 * geometry call + the N consensus read samples. We price that aggregate at
 * the row's primary `modelId` rate to surface an *estimate*; it is not a
 * billing figure (a cheaper geometry-model's share is priced at the read
 * model's rate, and provider invoices round/meter differently).
 *
 * Rates are USD per 1,000,000 tokens, transcribed from
 * `environment-matrix.md` §6 (captured 2026-06-03). Thinking tokens already
 * bill at the output rate and are folded into the recorded output total, so
 * no separate thinking line is needed here. Keep this table in sync with
 * that doc when prices or the default model change.
 */

type ModelRate = {
  readonly inputPerMillion: number;
  readonly outputPerMillion: number;
};

const MODEL_RATES: Readonly<Record<string, ModelRate>> = {
  // Gemini — KAN-53 panel-scan (current iTest provider).
  "gemini-3.1-pro-preview": { inputPerMillion: 2, outputPerMillion: 12 },
  "gemini-3.5-flash": { inputPerMillion: 1.5, outputPerMillion: 9 },
};

export type TokenUsage = {
  readonly inputTokens: number;
  readonly outputTokens: number;
};

/**
 * Estimated USD cost of one invocation, or `null` when it cannot be
 * estimated — a provider error left no usage to price (`usage === null`),
 * or the row's model has no rate in the table (a model added since this was
 * last synced). A `null` model lookup is deliberately surfaced as "no
 * estimate" rather than guessed.
 */
export function estimateCostUsd(
  usage: TokenUsage | null,
  modelId: string,
): number | null {
  if (usage === null) return null;
  const rate = MODEL_RATES[modelId];
  if (rate === undefined) return null;
  return (
    (usage.inputTokens / 1_000_000) * rate.inputPerMillion +
    (usage.outputTokens / 1_000_000) * rate.outputPerMillion
  );
}
